import { Box } from '@mui/material'
import React, { memo, useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import FormSelect from '../../../components/Forms/FormSelect'
import { IHsHeading, IHsSubheading } from '../types'
import { Option } from './hooks/useHsCode'
import messages from './messages'
import { formatHsHeadingDisplay, formatHsSubheadingDisplay } from './utils/hsCodeUtils'

interface HsCodeSelectorProps {
    readonly hsHeadingItems: IHsHeading[]
    readonly hsSubheadingItems: IHsSubheading[]
    readonly isDisabled?: boolean
    readonly isEdit?: boolean
    readonly hsLocalTariffsLength?: number
}

const HsCodeSelector: React.FC<HsCodeSelectorProps> = memo(({
    hsHeadingItems,
    hsSubheadingItems,
    isDisabled = false,
    isEdit = false,
    hsLocalTariffsLength = 0,
}) => {
    const { formatMessage } = useIntl()
    const { watch, setValue } = useFormContext()

    const hsHeadingId = watch('hsHeadingId')
    const hsHeading = watch('hsHeading')
    const hsSubheading = watch('hsSubheading')

    const mapToDropDownItems = useCallback((items: IHsHeading[] | IHsSubheading[]): Option[] => {
        return items.map((item) => {
            if ('subheadingCode' in item) {
                return {
                    value: item.id ?? 0,
                    label: formatHsSubheadingDisplay(item.subheadingCode, item.description),
                }
            } else {
                return {
                    value: item.id ?? 0,
                    label: formatHsHeadingDisplay(item.headingCode, item.description),
                }
            }
        })
    }, [])

    const getSubheadingItems = useCallback(
        (headingId?: number): IHsSubheading[] => {
            if (!headingId) return []
            return hsSubheadingItems.filter((item) => item.hsHeadingId === headingId)
        },
        [hsSubheadingItems]
    )

    const handleHeadingChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setValue('hsSubheadingId', undefined)
        return event
    }, [setValue])

    const headingOptions = useMemo(() => {
        if (isDisabled && hsHeadingItems.length === 0 && hsHeading?.id) {
            return [{
                value: hsHeading.id,
                label: formatHsHeadingDisplay(hsHeading.headingCode ?? '', hsHeading.description ?? ''),
            }]
        }
        return mapToDropDownItems(hsHeadingItems)
    }, [hsHeadingItems, mapToDropDownItems, isDisabled, hsHeading])

    const subheadingOptions = useMemo(() => {
        if (isDisabled && hsSubheadingItems.length === 0 && hsSubheading?.id) {
            return [{
                value: hsSubheading.id,
                label: formatHsSubheadingDisplay(hsSubheading.subheadingCode ?? '', hsSubheading.description ?? ''),
            }]
        }
        return mapToDropDownItems(getSubheadingItems(hsHeadingId))
    }, [hsHeadingId, getSubheadingItems, mapToDropDownItems, isDisabled, hsSubheadingItems.length, hsSubheading])

    const isHeadingDisabled = isDisabled || isEdit || hsLocalTariffsLength > 0
    const isSubheadingDisabled = isDisabled || isEdit || hsLocalTariffsLength > 0

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: '8px', width: '50%' }}>
                <FormSelect
                    name='hsHeadingId'
                    label={formatMessage(messages.hsHeading)}
                    options={headingOptions}
                    onChange={handleHeadingChange}
                    disabled={isHeadingDisabled}
                    required
                    fullWidth
                />
            </Box>
            <Box sx={{ mb: '8px', width: '50%' }}>
                <FormSelect
                    name='hsSubheadingId'
                    label={formatMessage(messages.hsSubheading)}
                    options={subheadingOptions}
                    disabled={isSubheadingDisabled}
                    required
                    fullWidth
                />
            </Box>
        </Box>
    )
})

HsCodeSelector.displayName = 'HsCodeSelector'

export default HsCodeSelector 